import { io, Socket } from 'socket.io-client';
import { SOCKET_EVENTS } from './socketEvents';

// Aligned with mobile App/Utils/SocketClient.ts
const getSocketUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL || '';
  return apiUrl.replace(/\/api\/v1\/?$/, '');
};

class SocketService {
  private socket: Socket | null = null;
  private pendingListeners = new Map<string, Set<(...args: any[]) => void>>();

  private attachPendingListeners() {
    if (!this.socket) return;
    this.pendingListeners.forEach((callbacks, event) => {
      callbacks.forEach((callback) => {
        this.socket!.on(event, callback);
      });
    });
  }

  connect() {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.log('No token found, cannot connect to socket');
        return;
      }

      if (this.socket?.connected) {
        return;
      }

      // Recreate if exists but disconnected
      if (this.socket) {
        this.socket.disconnect();
        this.socket = null;
      }

      this.socket = io(getSocketUrl(), {
        query: { token },
        transports: ['websocket'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5,
      });

      this.attachPendingListeners();

      this.socket.on('connect', () => {
        console.log('Socket connected successfully');
      });

      this.socket.on('disconnect', (reason) => {
        console.log('Socket disconnected:', reason);
      });

      this.socket.on('connect_error', (error) => {
        console.error('Socket connection error:', error);
      });
    } catch (error) {
      console.error('Error connecting to socket:', error);
    }
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  on(event: string, callback: (...args: any[]) => void) {
    if (this.socket) {
      this.socket.on(event, callback);
    }

    if (!this.pendingListeners.has(event)) {
      this.pendingListeners.set(event, new Set());
    }
    this.pendingListeners.get(event)!.add(callback);
  }

  off(event: string, callback?: (...args: any[]) => void) {
    if (callback) {
      this.pendingListeners.get(event)?.delete(callback);
    } else {
      this.pendingListeners.delete(event);
    }

    if (this.socket) {
      if (callback) {
        this.socket.off(event, callback);
      } else {
        this.socket.off(event);
      }
    }
  }

  emit(event: string, data?: any) {
    if (this.socket) {
      this.socket.emit(event, data);
    }
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  joinGroupSession(sessionId: string) {
    this.emit('group:join', { sessionId });
  }

  leaveGroupSession(sessionId: string) {
    this.emit('group:leave', { sessionId });
  }

  onMemberJoined(callback: (data: any) => void) {
    this.on(SOCKET_EVENTS.GROUP_MEMBER_JOINED, callback);
  }

  onMemberLeft(callback: (data: any) => void) {
    this.on(SOCKET_EVENTS.GROUP_MEMBER_LEFT, callback);
  }

  onHostChanged(callback: (data: any) => void) {
    this.on(SOCKET_EVENTS.GROUP_HOST_CHANGED, callback);
  }

  onItemAdded(callback: (data: any) => void) {
    this.on(SOCKET_EVENTS.GROUP_ITEM_ADDED, callback);
  }

  onItemUpdated(callback: (data: any) => void) {
    this.on(SOCKET_EVENTS.GROUP_ITEM_UPDATED, callback);
  }

  onItemRemoved(callback: (data: any) => void) {
    this.on(SOCKET_EVENTS.GROUP_ITEM_REMOVED, callback);
  }

  onCheckoutStarted(callback: (data: any) => void) {
    this.on(SOCKET_EVENTS.GROUP_CHECKOUT_STARTED, callback);
  }

  onCheckoutFailed(callback: (data: any) => void) {
    this.on(SOCKET_EVENTS.GROUP_CHECKOUT_FAILED, callback);
  }

  onSessionConfirmed(callback: (data: any) => void) {
    this.on(SOCKET_EVENTS.GROUP_SESSION_CONFIRMED, callback);
  }

  onSessionExpired(callback: (data: any) => void) {
    this.on(SOCKET_EVENTS.GROUP_SESSION_EXPIRED, callback);
  }

  removeAllListeners() {
    Object.values(SOCKET_EVENTS).forEach((event) => {
      this.off(event);
    });
  }
}

const socketService = new SocketService();
export default socketService;
